import { t } from 'i18next';
import * as yup from 'yup';

import {
  Question,
  QuestionCondition,
  QuestionOption,
  QuestionSetConfig,
  QuestionType,
} from '@/shared/types/action';

import { QUESTION_SET_LIMITS } from '../constants';

export const QUESTION_TYPE_LABEL_KEYS: Record<QuestionType, string> = {
  single: 'dialog.question_set.type_single',
  multi: 'dialog.question_set.type_multi',
  text: 'dialog.question_set.type_text',
};

// Dialog owns these context variables; an answer must not overwrite them.
const RESERVED_KEYS = ['tags', 'user', 'channels', 'campaign', 'global', 'live_instructions'];

const keyPattern = /^[a-z][a-z0-9_]*$/;
const valuePattern = /^[a-z0-9_]+$/;

const required = () => t('validation.required');
const lessThan = (max: number) => () => t('validation.less_than', { 0: max });

/** The questions before `index` that a `show_if` may point at. */
export const earlierChoiceQuestions = (questions: Question[], index: number) =>
  questions.slice(0, index).filter((question) => question.type !== 'text');

const conditionSchema = yup.object({
  key: yup.string().required(required),
  in: yup
    .array()
    .of(yup.string().required())
    .min(1, () => t('dialog.question_set.errors.show_if_values'))
    .max(QUESTION_SET_LIMITS.OPTIONS_MAX, () => t('dialog.question_set.errors.show_if_values'))
    .required(required),
});

const optionSchema = yup.object({
  label: yup
    .string()
    .trim()
    .required(required)
    .max(QUESTION_SET_LIMITS.LABEL, lessThan(QUESTION_SET_LIMITS.LABEL)),
  value: yup
    .string()
    .required(required)
    .matches(valuePattern, () => t('dialog.question_set.errors.value_pattern'))
    .max(QUESTION_SET_LIMITS.VALUE, lessThan(QUESTION_SET_LIMITS.VALUE)),
  description: yup
    .string()
    .trim()
    .max(QUESTION_SET_LIMITS.DESCRIPTION, lessThan(QUESTION_SET_LIMITS.DESCRIPTION)),
  recommended: yup.boolean(),
  show_if: conditionSchema.default(undefined),
});

const uniqueOptionFields = function (this: yup.TestContext, options?: QuestionOption[]) {
  const errors: yup.ValidationError[] = [];
  for (const field of ['label', 'value'] as const) {
    const seen = new Set<string>();
    options?.forEach((option, index) => {
      const value = option[field]?.trim();
      if (value && seen.has(value)) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${field}`,
            message: t('validation.unique_items'),
          })
        );
      }
      if (value) {
        seen.add(value);
      }
    });
  }
  return errors.length > 0 ? new yup.ValidationError(errors) : true;
};

const recommendedFirst = function (this: yup.TestContext, options?: QuestionOption[]) {
  const errors = (options ?? []).flatMap((option, index) =>
    index > 0 && option.recommended
      ? [
          this.createError({
            path: `${this.path}[${index}].recommended`,
            message: t('dialog.question_set.errors.recommended_first'),
          }),
        ]
      : []
  );
  return errors.length > 0 ? new yup.ValidationError(errors) : true;
};

const questionSchema = yup.object({
  key: yup
    .string()
    .required(required)
    .matches(keyPattern, () => t('dialog.question_set.errors.key_pattern'))
    .max(QUESTION_SET_LIMITS.KEY, lessThan(QUESTION_SET_LIMITS.KEY))
    .notOneOf(RESERVED_KEYS, ({ value }) =>
      t('dialog.question_set.errors.reserved_key', { 0: value })
    ),
  header: yup
    .string()
    .trim()
    .required(required)
    .max(QUESTION_SET_LIMITS.HEADER, lessThan(QUESTION_SET_LIMITS.HEADER)),
  question: yup
    .string()
    .trim()
    .required(required)
    .max(QUESTION_SET_LIMITS.QUESTION, lessThan(QUESTION_SET_LIMITS.QUESTION)),
  type: yup.string().oneOf(Object.keys(QUESTION_TYPE_LABEL_KEYS)).required(required),
  allow_other: yup.boolean(),
  options: yup.array().when('type', {
    is: 'text',
    then: (schema) => schema,
    otherwise: (schema) =>
      schema
        .of(optionSchema)
        .required(required)
        .min(QUESTION_SET_LIMITS.OPTIONS_MIN, () =>
          t('validation.min_items', { 0: QUESTION_SET_LIMITS.OPTIONS_MIN })
        )
        .max(QUESTION_SET_LIMITS.OPTIONS_MAX, () =>
          t('validation.max_items', { 0: QUESTION_SET_LIMITS.OPTIONS_MAX })
        )
        .test('unique-options', '', uniqueOptionFields)
        .test('recommended-first', '', recommendedFirst),
  }),
  show_if: conditionSchema.default(undefined),
});

const conditionError = (
  condition: QuestionCondition | undefined,
  earlier: Question[]
): { field: 'key' | 'in'; message: string } | undefined => {
  if (!condition) {
    return undefined;
  }
  const target = earlier.find((question) => question.key === condition.key);
  if (!target) {
    return { field: 'key', message: t('dialog.question_set.errors.show_if_earlier') };
  }
  const values = (target.options ?? []).map((option) => option.value);
  if (condition.in.some((value) => !values.includes(value))) {
    return { field: 'in', message: t('dialog.question_set.errors.show_if_values') };
  }
  return undefined;
};

/** Unique keys, plus every `show_if` names an earlier choice question and its values. */
const questionReferences = function (this: yup.TestContext, questions?: Question[]) {
  const errors: yup.ValidationError[] = [];
  const seen = new Set<string>();
  questions?.forEach((question, index) => {
    if (question.key && seen.has(question.key)) {
      errors.push(
        this.createError({
          path: `${this.path}[${index}].key`,
          message: t('validation.unique_items'),
        })
      );
    }
    seen.add(question.key);

    const earlier = earlierChoiceQuestions(questions, index);
    const conditions = [
      { path: `${this.path}[${index}].show_if`, condition: question.show_if },
      ...(question.options ?? []).map((option, optionIndex) => ({
        path: `${this.path}[${index}].options[${optionIndex}].show_if`,
        condition: option.show_if,
      })),
    ];
    for (const { path, condition } of conditions) {
      const error = conditionError(condition, earlier);
      if (error) {
        errors.push(this.createError({ path: `${path}.${error.field}`, message: error.message }));
      }
    }
  });
  return errors.length > 0 ? new yup.ValidationError(errors) : true;
};

export const questionSetSchema = yup.object({
  title: yup
    .string()
    .trim()
    .required(required)
    .max(QUESTION_SET_LIMITS.TITLE, lessThan(QUESTION_SET_LIMITS.TITLE)),
  questions: yup
    .array()
    .of(questionSchema)
    .required(required)
    .min(1, () => t('validation.min_items', { 0: 1 }))
    .max(QUESTION_SET_LIMITS.QUESTIONS, () =>
      t('validation.max_items', { 0: QUESTION_SET_LIMITS.QUESTIONS })
    )
    .test('question-references', '', questionReferences),
  trigger_node_id: yup.string(),
});

export type QuestionSetForm = QuestionSetConfig & { trigger_node_id: string };

const mapConditions = (
  questions: Question[],
  map: (condition: QuestionCondition, questionIndex: number) => QuestionCondition | undefined
): Question[] => {
  const apply =
    (questionIndex: number) =>
    <T extends { show_if?: QuestionCondition }>(item: T): T => {
      if (!item.show_if) {
        return item;
      }
      const next = map(item.show_if, questionIndex);
      return next === item.show_if ? item : { ...item, show_if: next };
    };
  return questions.map((question, index) => {
    const cleaned = apply(index)(question);
    return cleaned.options ? { ...cleaned, options: cleaned.options.map(apply(index)) } : cleaned;
  });
};

const hasKey = (questions: Question[], key: string) =>
  questions.some((question) => question.key === key);

const hasOptionValue = (questions: Question[], key: string, value: string) =>
  questions.some(
    (question) => question.key === key && question.options?.some((option) => option.value === value)
  );

/** Drops every condition on a deleted question's key, unless a question still has that key. */
export const withoutQuestionReferences = (questions: Question[], key: string): Question[] =>
  hasKey(questions, key)
    ? questions
    : mapConditions(questions, (condition) => (condition.key === key ? undefined : condition));

/**
 * Drops a deleted option value from every condition on its question, unless an option there
 * still has it. An emptied condition goes too.
 */
export const withoutOptionValueReferences = (
  questions: Question[],
  key: string,
  value: string
): Question[] =>
  hasOptionValue(questions, key, value)
    ? questions
    : mapConditions(questions, (condition) => {
        if (condition.key !== key || !condition.in.includes(value)) {
          return condition;
        }
        const rest = condition.in.filter((v) => v !== value);
        return rest.length > 0 ? { ...condition, in: rest } : undefined;
      });

/** Points every condition on key `from` at `to`, unless a question still has `from`. */
export const renameQuestionReferences = (questions: Question[], from: string, to: string) =>
  hasKey(questions, from)
    ? questions
    : mapConditions(questions, (condition) =>
        condition.key === from ? { ...condition, key: to } : condition
      );

/** Points every condition on question `key` at `to`, unless an option there still has `from`. */
export const renameOptionValueReferences = (
  questions: Question[],
  key: string,
  from: string,
  to: string
): Question[] =>
  hasOptionValue(questions, key, from)
    ? questions
    : mapConditions(questions, (condition) =>
        condition.key === key && condition.in.includes(from)
          ? { ...condition, in: condition.in.map((v) => (v === from ? to : v)) }
          : condition
      );

/**
 * Drops a `show_if` that names a question of the set which is no longer an earlier choice
 * question (it moved below, or became text), after a reorder or type change. A key the set does
 * not know at all stays, so a rename in progress gets a validation message, not a silent clear.
 */
export const clearStaleConditions = (questions: Question[]): Question[] => {
  const allKeys = questions.map(({ key }) => key);
  return mapConditions(questions, (condition, index) => {
    const earlierKeys = earlierChoiceQuestions(questions, index).map(({ key }) => key);
    return allKeys.includes(condition.key) && !earlierKeys.includes(condition.key)
      ? undefined
      : condition;
  });
};
